import { round } from 'lodash'
import { DataStore, SortDirection } from '@aws-amplify/datastore'
import { Quote, QuoteInfo, RecapInfo } from '../../models'
import { categories } from '../quoteGridUtils/createQuoteUtil'
import { fetchQuoteInfoUtil, fetchRecapInfoUtil } from '@src/utils/fetchUtils'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'

function getQuoteCategories(quoteId) {
  return categories
}

async function getQuoteTotals({ quoteId, fetchFilter }) {
  // Finds quote from the id
  const quoteDetails = await DataStore.query(Quote, item =>
    item.id('eq', quoteId).activeFlag('eq', true)
  )
  const quote = JSON.parse(JSON.stringify(quoteDetails))
  const quoteInfoByQuoteId = await DataStore.query(
    QuoteInfo,
    row => row.quoteId('eq', quoteId).activeFlag('eq', true),
    {
      sort: row => row.tableRowIndex(SortDirection.ASCENDING),
    }
  )
  const quoteInfo = JSON.parse(JSON.stringify(quoteInfoByQuoteId))
  const recapQuoteInfoByQuoteId = await DataStore.query(
    RecapInfo,
    row => row.quoteId('eq', quoteId).activeFlag('eq', true),
    {
      sort: row => row.tableRowIndex(SortDirection.ASCENDING),
    }
  )
  const recapInfo = JSON.parse(JSON.stringify(recapQuoteInfoByQuoteId))
  // create a totals object for the quote
  const totalsObject = {
    id: null,
    name: null,
    categoryTotals: [],
    franceCategoryTotals: [],
    internationalCategoryTotals: [],
    totals: {},
    franceTotals: {},
    internationalTotals: {},
    discount: null,
  }
  // if quote doesn't exist it sends back the empty totalsObject
  if (!quote) {
    return totalsObject
  }
  // set name and id of quote on totals object
  totalsObject.id = quote.id
  totalsObject.name = quote.name
  // initialize the quote totals object
  const totals = getTotalTemplateObject()
  const franceTotals = getTotalTemplateObject()
  const internationalTotals = getTotalTemplateObject()
  // get the category totals template array for all categories
  const categoryTotalsArray = getCategoryTotalsTemplateArray(quoteId, null)
  const franceCategoryTotalsArray = getCategoryTotalsTemplateArray(quoteId, 'f')
  const internationalCategoryTotalsArray = getCategoryTotalsTemplateArray(quoteId, 'i')
  const allTotalsObject = {
    totals,
    franceTotals,
    internationalTotals,
    categoryTotalsArray,
    franceCategoryTotalsArray,
    internationalCategoryTotalsArray,
  }
  // get all the category total rows from quote Info, so at this point we have the latest budget total information
  // also initialize the provisionalTotal and actualTotal to be 0 for all these categories.
  let categoryTotalRows = quoteInfo
    .filter(row => row.costLineType === 'categoryTotal')
    .map(totalRow => {
      const localTotalRow = { ...totalRow }
      localTotalRow.provisionalTotal = 0
      localTotalRow.actualTotal = 0
      return localTotalRow
    })

  // if grid2 / recapInfo has been populated, we might have differet provisional and actual total information
  if (recapInfo && recapInfo.length) {
    // get all the category total rows in recap info (essentially all categories that have totals)
    const recapInfoCategoryTotalRows = recapInfo.filter(row => row.costLineType === 'categoryTotal')
    console.log('recapInfoCategoryTotalRows: ', recapInfoCategoryTotalRows)
    // Sets the the category totals for provisional and actual we got from above to the categoryTotalRows
    categoryTotalRows = categoryTotalRows.map(totalRow => {
      const localTotalRow = { ...totalRow }
      // Finds the index in recapInfoCategory Totals where the current category total name matches with recapCategoryTotal
      const recapInfoCategoryTotalRowIndex = recapInfoCategoryTotalRows.findIndex(
        recapInfoTotalRow => totalRow.category === recapInfoTotalRow.category
      )
      // If index was found we set the current categories provisional and actual total to that of recapInfo
      if (recapInfoCategoryTotalRowIndex >= 0) {
        const { provisionalTotal, actualTotal } =
          recapInfoCategoryTotalRows[recapInfoCategoryTotalRowIndex]
        console.log(
          'Category: ',
          recapInfoCategoryTotalRows[recapInfoCategoryTotalRowIndex].category
        )
        console.log('actualTotal: ', actualTotal)
        localTotalRow.provisionalTotal = provisionalTotal
        localTotalRow.actualTotal = actualTotal
      }
      return localTotalRow
    })
  }

  // loop over the categoryTotalRows -
  // 1. update the categoryTotalsArray (templateArray) with correct total values
  // 2. update the totalsObjects (categoryTotalsArray, totals, franceTotals, internationalTotals) of the quote with the correct totals
  const finalAllTotalsObject = categoryTotalRows.reduce((allTotalsObject, totalRow, index) => {
    // allTotalsObject is defined above with all relevant details
    // Finds the index of the category index where
    const categoryTotalsArrayIndex = allTotalsObject.categoryTotalsArray.findIndex(
      row => row.category === totalRow.category.replace(' - EXE', '')
    )
    if (!totalRow.isInternational) {
      // if it is a france category
      const franceCategoryTotalsArrayIndex = allTotalsObject.franceCategoryTotalsArray.findIndex(
        row => row.category === totalRow.category.replace(' - EXE', '')
      )
      // If the catergoryTotalsArray has the index for the totals we save the totals value in allTotalsObject
      // provisional & actualTotal are set to 0 if not present
      if (categoryTotalsArrayIndex >= 0) {
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].budgetTotal = totalRow.total
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].provisionalTotal =
          totalRow.provisionalTotal
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].actualTotal =
          totalRow.actualTotal
      }
      if (franceCategoryTotalsArrayIndex >= 0) {
        allTotalsObject.franceCategoryTotalsArray[franceCategoryTotalsArrayIndex].budgetTotal =
          totalRow.total
        allTotalsObject.franceCategoryTotalsArray[franceCategoryTotalsArrayIndex].provisionalTotal =
          totalRow.provisionalTotal
        allTotalsObject.franceCategoryTotalsArray[franceCategoryTotalsArrayIndex].actualTotal =
          totalRow.actualTotal
      }
    } else {
      // if it is an international category
      const internationalCategoryTotalsArrayIndex =
        allTotalsObject.internationalCategoryTotalsArray.findIndex(
          row => row.category === totalRow.category.replace(' - EXE', '')
        )
      // for the normal totals array, add the international totals to the existing france totals
      if (categoryTotalsArrayIndex >= 0) {
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].budgetTotal += totalRow.total
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].provisionalTotal +=
          totalRow.provisionalTotal
        allTotalsObject.categoryTotalsArray[categoryTotalsArrayIndex].actualTotal +=
          totalRow.actualTotal
      }
      if (internationalCategoryTotalsArrayIndex >= 0) {
        allTotalsObject.internationalCategoryTotalsArray[
          internationalCategoryTotalsArrayIndex
        ].budgetTotal = totalRow.total

        allTotalsObject.internationalCategoryTotalsArray[
          internationalCategoryTotalsArrayIndex
        ].provisionalTotal = totalRow.provisionalTotal

        allTotalsObject.internationalCategoryTotalsArray[
          internationalCategoryTotalsArrayIndex
        ].actualTotal = totalRow.actualTotal
      }
    }

    if (totalRow.total) {
      allTotalsObject.totals.budgetSubTotal += totalRow.total
      allTotalsObject.totals.budgetGrandTotal += totalRow.total
      // actual and provisional grand total are always the same as bugetGrandTotal
      allTotalsObject.totals.actualGrandTotal += totalRow.total
      allTotalsObject.totals.provisionalGrandTotal += totalRow.total
      if (totalRow.isInternational) {
        allTotalsObject.internationalTotals.budgetSubTotal += totalRow.total
        allTotalsObject.internationalTotals.budgetGrandTotal += totalRow.total
        // actual and provisional grand total are always the same as bugetGrandTotal
        allTotalsObject.internationalTotals.actualGrandTotal += totalRow.total
        allTotalsObject.internationalTotals.provisionalGrandTotal += totalRow.total
      } else {
        allTotalsObject.franceTotals.budgetSubTotal += totalRow.total
        allTotalsObject.franceTotals.budgetGrandTotal += totalRow.total
        // actual and provisional grand total are always the same as bugetGrandTotal
        allTotalsObject.franceTotals.actualGrandTotal += totalRow.total
        allTotalsObject.franceTotals.provisionalGrandTotal += totalRow.total
      }
    }
    if (totalRow.actualTotal) {
      allTotalsObject.totals.actualSubTotal += totalRow.actualTotal
      if (totalRow.isInternational) {
        allTotalsObject.internationalTotals.actualSubTotal += totalRow.actualTotal
      } else {
        allTotalsObject.franceTotals.actualSubTotal += totalRow.actualTotal
      }
    }
    if (totalRow.provisionalTotal) {
      allTotalsObject.totals.provisionalSubTotal += totalRow.provisionalTotal
      if (totalRow.isInternational) {
        allTotalsObject.internationalTotals.provisionalSubTotal += totalRow.provisionalTotal
      } else {
        allTotalsObject.franceTotals.provisionalSubTotal += totalRow.provisionalTotal
      }
    }
    if (totalRow.totalMargin) {
      allTotalsObject.totals.budgetMargin += totalRow.totalMargin
      allTotalsObject.totals.budgetGrandTotal += totalRow.totalMargin
      // add the budget margin to actual grand total only when actual grand total exists
      if (allTotalsObject.totals.actualGrandTotal)
        allTotalsObject.totals.actualGrandTotal += totalRow.totalMargin
      // add the budget margin to provisional grand total only when provisional grand total exists
      if (allTotalsObject.totals.provisionalGrandTotal)
        allTotalsObject.totals.provisionalGrandTotal += totalRow.totalMargin
      if (totalRow.isInternational) {
        allTotalsObject.internationalTotals.budgetMargin += totalRow.totalMargin
        allTotalsObject.internationalTotals.budgetGrandTotal += totalRow.totalMargin
        // add the budget margin to actual grand total only when actual grand total exists
        if (allTotalsObject.internationalTotals.actualGrandTotal)
          allTotalsObject.internationalTotals.actualGrandTotal += totalRow.totalMargin
        // add the budget margin to provisional grand total only when provisional grand total exists
        if (allTotalsObject.internationalTotals.provisionalGrandTotal)
          allTotalsObject.internationalTotals.provisionalGrandTotal += totalRow.totalMargin
      } else {
        allTotalsObject.franceTotals.budgetMargin += totalRow.totalMargin
        allTotalsObject.franceTotals.budgetGrandTotal += totalRow.totalMargin
        // add the budget margin to actual grand total only when actual grand total exists
        if (allTotalsObject.franceTotals.actualGrandTotal)
          allTotalsObject.franceTotals.actualGrandTotal += totalRow.totalMargin
        // add the budget margin to provisional grand total only when provisional grand total exists
        if (allTotalsObject.franceTotals.provisionalGrandTotal)
          allTotalsObject.franceTotals.provisionalGrandTotal += totalRow.totalMargin
      }
    }
    return allTotalsObject
  }, allTotalsObject)

  // update the category totals array
  totalsObject.categoryTotals = finalAllTotalsObject.categoryTotalsArray
  totalsObject.franceCategoryTotals = finalAllTotalsObject.franceCategoryTotalsArray
  totalsObject.internationalCategoryTotals = finalAllTotalsObject.internationalCategoryTotalsArray
  delete finalAllTotalsObject.categoryTotalsArray
  delete finalAllTotalsObject.franceCategoryTotalsArray
  delete finalAllTotalsObject.internationalCategoryTotalsArray

  // calculate all margins in the totalsObjects and then round of all totals to two decimal places
  for (const key in finalAllTotalsObject) {
    finalAllTotalsObject[key] = calculateMarginsAndReturnUpdatedTotals(finalAllTotalsObject[key])
    for (const totalKey in finalAllTotalsObject[key]) {
      finalAllTotalsObject[key][totalKey] = round(finalAllTotalsObject[key][totalKey], 2)
    }
  }

  // assign the final totals to the actual totalsObject
  totalsObject.totals = finalAllTotalsObject.totals
  totalsObject.franceTotals = finalAllTotalsObject.franceTotals
  totalsObject.internationalTotals = finalAllTotalsObject.internationalTotals

  if (quote.discount && quote.discount.totalDiscount) {
    // get the discount object
    const quoteDiscountObject = { ...quote.discount }
    const discount = getDiscountTemplateObject()

    // total after discount and discount is the same in cases
    discount.totalAfterDiscount = quoteDiscountObject.totalAfterDiscount
    discount.totalDiscount = quoteDiscountObject.totalDiscount
    // budget discount values
    discount.budgetTotalDiscountOnMargin = quoteDiscountObject.totalDiscountOnMargin
    discount.budgetTotalDiscountOnMarginPercentage =
      quoteDiscountObject.totalDiscountOnMarginPercentage
    discount.budgetTotalDiscountPercentage = quoteDiscountObject.totalDiscountPercentage

    // provisional discount values
    discount.provisionalTotalDiscountOnMargin = round(
      totalsObject.totals.provisionalMargin - discount.totalDiscount,
      2
    )
    discount.provisionalTotalDiscountOnMarginPercentage = round(
      (discount.provisionalTotalDiscountOnMargin / discount.totalAfterDiscount) * 100,
      2
    )
    discount.provisionalTotalDiscountPercentage = round(
      totalsObject.totals.provisionalMarginPercentage -
        discount.provisionalTotalDiscountOnMarginPercentage,
      2
    )

    // actual discount values
    discount.actualTotalDiscountOnMargin = round(
      totalsObject.totals.actualMargin - discount.totalDiscount,
      2
    )
    discount.actualTotalDiscountOnMarginPercentage = round(
      (discount.actualTotalDiscountOnMargin / discount.totalAfterDiscount) * 100,
      2
    )
    discount.actualTotalDiscountPercentage = round(
      totalsObject.totals.actualMarginPercentage - discount.actualTotalDiscountOnMarginPercentage,
      2
    )

    totalsObject.discount = discount
  } else {
    const discount = getDiscountTemplateObject()
    // total after discount and discount is the same in cases
    discount.totalAfterDiscount = totalsObject.totals.budgetGrandTotal
    totalsObject.discount = discount
  }

  return totalsObject
}

function calculateMarginsAndReturnUpdatedTotals(quoteTotals) {
  if (quoteTotals.budgetGrandTotal)
    quoteTotals.budgetMarginPercentage =
      (quoteTotals.budgetMargin / quoteTotals.budgetGrandTotal) * 100
  const diffBetweenBudgetAndActual = quoteTotals.budgetSubTotal - quoteTotals.actualSubTotal
  quoteTotals.actualMargin = quoteTotals.budgetMargin + diffBetweenBudgetAndActual
  if (quoteTotals.budgetGrandTotal)
    quoteTotals.actualMarginPercentage =
      (quoteTotals.actualMargin / quoteTotals.budgetGrandTotal) * 100
  const diffBetweenBudgetAndProvisional =
    quoteTotals.budgetSubTotal - quoteTotals.provisionalSubTotal
  quoteTotals.provisionalMargin = quoteTotals.budgetMargin + diffBetweenBudgetAndProvisional
  if (quoteTotals.budgetGrandTotal)
    quoteTotals.provisionalMarginPercentage =
      (quoteTotals.provisionalMargin / quoteTotals.budgetGrandTotal) * 100
  return quoteTotals
}

function getDiscountTemplateObject() {
  return {
    totalAfterDiscount: 0,
    totalDiscount: 0,
    budgetTotalDiscountOnMargin: 0,
    budgetTotalDiscountOnMarginPercentage: 0,
    budgetTotalDiscountPercentage: 0,
    provisionalTotalDiscountOnMargin: 0,
    provisionalTotalDiscountOnMarginPercentage: 0,
    provisionalTotalDiscountPercentage: 0,
    actualTotalDiscountOnMargin: 0,
    actualTotalDiscountOnMarginPercentage: 0,
    actualTotalDiscountPercentage: 0,
  }
}

function getTotalTemplateObject() {
  return {
    budgetSubTotal: 0,
    budgetGrandTotal: 0,
    budgetMargin: 0,
    budgetMarginPercentage: 0,
    provisionalSubTotal: 0,
    provisionalGrandTotal: 0,
    provisionalMargin: 0,
    provisionalMarginPercentage: 0,
    actualSubTotal: 0,
    actualGrandTotal: 0,
    actualMargin: 0,
    actualMarginPercentage: 0,
  }
}

function getCategoryTotalsTemplateArray(quoteId, franceOrInternational) {
  let categories = getQuoteCategories(quoteId)
  if (franceOrInternational) {
    if (franceOrInternational === 'i') {
      // get only the international categories when explicit asked for international categories
      categories = categories.filter(c => c.isInternational)
    } else {
      // keep only the france categories as they represent all the unique categories
      categories = categories.filter(c => !c.isInternational)
    }
  } else {
    // get only the france categories as they represent all the unique categories
    categories = categories.filter(c => !c.isInternational)
  }
  // the name is normalized by replacing ' - EXE' with '' in the map below to make sure all the categories have the same name
  return categories.map(c => {
    return {
      isInternational: franceOrInternational ? c.isInternational : null,
      categoryPrefix: c.categoryPrefix,
      categoryRoman: c.categoryPrefix.substring(0, c.categoryPrefix.length - 1),
      categoryId: c.categoryId,
      category: c.category.replace(' - EXE', ''),
      category_fr: c.category_fr.replace(' - EXE', ''),
      budgetTotal: 0,
      provisionalTotal: 0,
      actualTotal: 0,
    }
  })
}

export { getQuoteTotals, getQuoteCategories }
